import type { ClientAssignedProgramView, ClientProgramPosition } from "@/types/programCenteredYaas";

const EXCLUDED_POSITION_STATUSES = new Set(["Disabled", "Hidden", "Ended"]);

export interface ProgramDashboardItem {
    id: string;
    name: string;
    description: string | null;
    status: string;
    surfaceCount: number;
    positionCount: number;
    userCount: number;
    totalHeld: number;
    totalRewards: number;
}

export interface ProgramDashboardModel {
    programs: ProgramDashboardItem[];
    totals: {
        programCount: number;
        userCount: number;
        totalHeld: number;
        totalRewards: number;
    };
}

function amount(value: string | null | undefined): number {
    if (!value) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function isVisiblePosition(position: ClientProgramPosition): boolean {
    return !EXCLUDED_POSITION_STATUSES.has(position.status);
}

function activeEndUserClientRunViews(views: ClientAssignedProgramView[]): ClientAssignedProgramView[] {
    return views.filter(
        (view) =>
            view.program.runType === "ClientRun" &&
            view.program.participationModel === "ClientEndUserEnrollments" &&
            view.program.status === "Active"
    );
}

function itemFromView(view: ClientAssignedProgramView): ProgramDashboardItem {
    const positions = view.positions.filter(isVisiblePosition);
    const userIds = new Set(positions.map((position) => position.participantAccountUuid));

    return {
        id: view.program.uuid,
        name: view.program.name,
        description: view.program.description,
        status: view.program.status,
        surfaceCount: view.surfaces.length,
        positionCount: positions.length,
        userCount: userIds.size,
        totalHeld: positions.reduce((sum, position) => sum + amount(position.manualUsdvAmount), 0),
        totalRewards: positions.reduce((sum, position) => sum + amount(position.totalRewards), 0)
    };
}

export function buildProgramDashboardModel(views: ClientAssignedProgramView[]): ProgramDashboardModel {
    const programs = activeEndUserClientRunViews(views).map(itemFromView);
    const allUsers = new Set<string>();

    for (const view of activeEndUserClientRunViews(views)) {
        for (const position of view.positions.filter(isVisiblePosition)) {
            allUsers.add(position.participantAccountUuid);
        }
    }

    return {
        programs,
        totals: {
            programCount: programs.length,
            userCount: allUsers.size,
            totalHeld: programs.reduce((sum, program) => sum + program.totalHeld, 0),
            totalRewards: programs.reduce((sum, program) => sum + program.totalRewards, 0)
        }
    };
}
